import { useEffect, useState, type KeyboardEvent } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  CalendarDays,
  FileText,
  HardDrive,
  LayoutDashboard,
  LayoutTemplate,
  Lock,
  LogOut,
  MessageCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Settings,
  SunMoon,
  Ticket,
  UserRound,
  Users2,
} from 'lucide-react';
import { PG_INT_MAX, UI_LAYOUT_LABELS, UI_LAYOUTS, type UiLayout } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { NAV_ANY_OF } from '@/shared/nav-permissions';
import { performLogout } from '@/auth/logout';
import { NotificationsBell } from '@/shared/notifications-bell';
import { NewsMegaphone } from '@/features/novidades/NewsMegaphone';
import { AppBrandMark } from '@/shared/brand-mark';
import { ticketSearchHint, useTenantProfile } from '@/shared/tenant-profile';
import { SIMPLIFIED_LAYOUT_AVAILABLE, useUiPrefs } from '@/shared/ui-prefs';
import { useRealtimeSync } from '@/shared/use-realtime-sync';
import { installNotificationSoundPriming } from '@/shared/notification-sound';
import { ChatsNavBadge } from '@/features/chats/chats-unread-badge';
import { PresenceChip } from '@/features/presence/presence-chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { TicketsSearch } from '@/features/tickets/TicketsPage';
import { cn } from '@/lib/utils';
import {
  clearSelectedTenant,
  getKnownTenants,
  getSelectedTenant,
  tenantLabel,
} from '@/lib/tenant-selection';

const COLLAPSE_KEY = 'erp.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0');
  } catch {
    /* armazenamento bloqueado: o servidor continua sendo a fonte */
  }
}

// `anyOf` (v7 — grupos de permissões): o item some da sidebar quando
// me.permissions veio SEM nenhuma das keys (can() do usePermissions; admin e
// usuário sem grupo veem tudo). Cofre fica: cada cofre tem permissão própria.
// Keys tipadas (satisfies PermissionKey[]) — typo/rename quebra o typecheck.
// v12 (padrão TiFlux): a sidebar volta a 11 itens. Pré-tickets virou a view
// `?view=pretickets` de /tickets (é triagem de ticket, não uma área) e
// Apontamentos saiu do menu — /appointments segue viva, aberta pelos
// relatórios e pela aba do ticket.
// spec 058: todo item tem a permissão da área (shared/nav-permissions) — quem só
// atende conversas vê só os Chats; quem não está em grupo nenhum vê tudo, como antes.
const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true, anyOf: NAV_ANY_OF.dashboard },
  { to: '/tickets', label: 'Tickets', icon: Ticket, anyOf: NAV_ANY_OF.tickets },
  { to: '/chats', label: 'Chats', icon: MessageCircle, anyOf: NAV_ANY_OF.chats },
  { to: '/clients', label: 'Clientes', icon: Users2, anyOf: NAV_ANY_OF.clients },
  { to: '/resources', label: 'Recursos', icon: HardDrive, anyOf: NAV_ANY_OF.resources },
  { to: '/contracts', label: 'Contratos', icon: FileText, anyOf: NAV_ANY_OF.contracts },
  { to: '/agenda', label: 'Agenda', icon: CalendarDays, anyOf: NAV_ANY_OF.agenda },
  { to: '/reports', label: 'Relatórios', icon: BarChart3, anyOf: NAV_ANY_OF.reports },
  { to: '/knowledge', label: 'Conhecimentos', icon: BookOpen, anyOf: NAV_ANY_OF.knowledge },
  { to: '/vault', label: 'Cofre de senhas', icon: Lock, anyOf: NAV_ANY_OF.vault },
  // v7: Mesas/Grupos/Catálogos/Modelos/WhatsApp saíram da sidebar — viram
  // itens do hub Configurações (padrão TiFlux: último item)
  { to: '/settings', label: 'Configurações', icon: Settings, anyOf: NAV_ANY_OF.settings },
] as const;

/**
 * Título da topbar: `staticData.title` do match mais profundo que o definir
 * (declarado rota a rota no router.tsx; função quando depende de params).
 */
function useMatchTitle(): string {
  return useRouterState({
    select: (s) => {
      for (let i = s.matches.length - 1; i >= 0; i--) {
        const match = s.matches[i];
        if (!match) continue;
        const title = match.staticData.title;
        if (title === undefined) continue;
        return typeof title === 'function' ? title(match.params as Record<string, string>) : title;
      }
      return 'Dashboard';
    },
  });
}

export function AppLayout() {
  // Stream SSE do realtime: vive enquanto o app autenticado estiver montado
  // (logout desmonta o layout ⇒ aborta a conexão).
  useRealtimeSync();
  // Autoplay: o AudioContext do ding só pode nascer dentro de um gesto do
  // usuário — o priming espera o primeiro clique/tecla nesta aba.
  useEffect(() => installNotificationSoundPriming(), []);
  const user = useAuthUser();
  const { can, ready: permissionsReady } = usePermissions();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const title = useMatchTitle();
  const tenantProfile = useTenantProfile();
  const ui = useUiPrefs();
  // spec 017: a barra recolhida segue a pessoa (servidor); o localStorage fica
  // só como ponto de partida até o /me chegar, para a barra não "pular"
  const [collapsed, setCollapsed] = useState(() => readCollapsed());
  const serverCollapsed = ui.prefs?.sidebarCollapsed;
  useEffect(() => {
    if (serverCollapsed === undefined) return;
    setCollapsed(serverCollapsed);
    writeCollapsed(serverCollapsed);
  }, [serverCollapsed]);
  const [ticketQuery, setTicketQuery] = useState('');

  const initial = (user?.name?.trim().charAt(0) ?? '?').toUpperCase();
  const searchHint = ticketSearchHint(tenantProfile);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
    ui.update({ sidebarCollapsed: next });
  };

  const openNewTicket = () =>
    // O board lê ?new=1 e abre o formulário de novo ticket (TicketsPage).
    void navigate({
      to: '/tickets',
      search: (prev: Record<string, unknown>) => ({ ...prev, new: 1 }) as unknown as TicketsSearch,
    });

  // Número puro (ou `#123`) abre o ticket, como sempre; qualquer outra coisa —
  // nome, placa, telefone formatado — abre a busca agrupada por mesa (spec 012).
  // Número acima do int4 não pode ser ticket (a API responderia 422): todo
  // telefone com DDD colado (`31972547754`) cai aqui e vai para a busca. Um
  // número curto (`72547754`) continua indo ao ticket, como sempre foi.
  const onTicketSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const raw = ticketQuery.trim();
    if (!raw) return;
    const number = raw.replace(/^#/, '');
    setTicketQuery('');
    if (/^\d+$/.test(number) && Number(number) <= PG_INT_MAX) {
      void navigate({ to: '/tickets/$ticketNumber', params: { ticketNumber: number } });
      return;
    }
    void navigate({ to: '/tickets/search', search: { q: raw } });
  };

  const handleLogout = () => {
    // Modo keycloak: end-session no Keycloak; modo local: volta pro /login.
    performLogout(() => void navigate({ to: '/login' }));
  };

  const isActive = (item: (typeof NAV)[number]) =>
    'exact' in item && item.exact
      ? pathname === item.to
      : pathname === item.to || pathname.startsWith(`${item.to}/`);

  // Sem nenhuma das keys do item ⇒ ESCONDE (a API é quem barra de verdade).
  // Enquanto o /me não resolveu, itens gated ficam escondidos — aparecer
  // depois é melhor que aparecer-e-sumir.
  // spec 058: o botão de novo ticket e a busca são de quem vê tickets
  const ticketsTeam = permissionsReady && NAV_ANY_OF.newTicket.some((key) => can(key));
  const navItems = NAV.filter(
    (item) => !('anyOf' in item) || (permissionsReady && item.anyOf.some((key) => can(key)))
  );

  return (
    <TooltipProvider>
      <div className="flex h-dvh overflow-hidden bg-background">
        {/* ── Sidebar petróleo ─────────────────────────────────────────────── */}
        <aside
          className={cn(
            'flex h-full shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-200',
            collapsed ? 'w-16' : 'w-56'
          )}
        >
          <div
            className={cn('flex h-14 shrink-0 items-center', collapsed ? 'justify-center' : 'px-5')}
          >
            <Link to="/" aria-label="Ir para o Dashboard" className="min-w-0">
              <AppBrandMark compact={collapsed} className="text-[21px] text-sidebar-foreground" />
            </Link>
          </div>

          <div className={cn('pb-2', collapsed ? 'px-2' : 'px-3')}>
            {!ticketsTeam ? null : collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={openNewTicket}
                    aria-label="Abrir novo ticket"
                    className="flex h-9 w-full items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary-hover active:bg-primary-pressed"
                  >
                    <Plus className="size-4" strokeWidth={2.5} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Abrir novo ticket</TooltipContent>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={openNewTicket}
                className="flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover active:bg-primary-pressed"
              >
                <Plus className="size-4" strokeWidth={2.5} />
                Abrir novo ticket
              </button>
            )}
          </div>

          <nav className={cn('mt-1 min-h-0 flex-1 overflow-y-auto', collapsed ? 'px-2' : 'px-2')}>
            <ul className="flex flex-col gap-0.5">
              {navItems.map((item) => {
                const active = isActive(item);
                const link = (
                  <Link
                    to={item.to}
                    className={cn(
                      'relative flex h-10 items-center gap-3 rounded-md text-sm font-medium transition-colors',
                      collapsed ? 'justify-center px-0' : 'px-3',
                      active
                        ? 'bg-white/10 text-white'
                        : 'text-white/75 hover:bg-white/5 hover:text-white'
                    )}
                  >
                    <item.icon className="size-[18px] shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                    {item.to === '/chats' ? <ChatsNavBadge collapsed={collapsed} /> : null}
                  </Link>
                );
                return (
                  <li key={item.to}>
                    {collapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{link}</TooltipTrigger>
                        <TooltipContent side="right">{item.label}</TooltipContent>
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Bloco do usuário */}
          <div
            className={cn(
              'flex shrink-0 items-center gap-2.5 border-t border-white/10 py-3',
              collapsed ? 'justify-center px-0' : 'px-4'
            )}
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              {initial}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">{user?.name}</div>
                <div className="truncate text-xs text-white/50">{user?.email}</div>
              </div>
            )}
          </div>

          {/* Minimizar */}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expandir menu' : 'Minimizar menu'}
            className={cn(
              'flex h-11 shrink-0 items-center gap-2.5 bg-sidebar-footer text-[13px] text-white/70 transition-colors hover:text-white',
              collapsed ? 'justify-center px-0' : 'px-4'
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <>
                <PanelLeftClose className="size-4" />
                Minimizar
              </>
            )}
          </button>
        </aside>

        {/* ── Coluna principal ─────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Topbar */}
          <header className="flex h-12 shrink-0 items-stretch justify-between border-b bg-card">
            <div className="flex min-w-0 items-end pl-4">
              <div className="flex h-9 min-w-0 max-w-[420px] items-center rounded-t-md bg-background px-4">
                <span className="truncate text-[13px] font-medium text-foreground">{title}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-4 pr-4">
              <div className={cn('relative hidden items-center', ticketsTeam && 'sm:flex')}>
                <input
                  value={ticketQuery}
                  onChange={(e) => setTicketQuery(e.target.value)}
                  onKeyDown={onTicketSearchKeyDown}
                  placeholder={`Nº, ${searchHint}`}
                  aria-label={`Buscar ticket pelo número, ${searchHint}`}
                  className="h-8 w-56 border-b border-border bg-transparent px-1 pr-7 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary"
                />
                <Search className="pointer-events-none absolute right-1 size-4 text-muted-foreground" />
              </div>
              {/* spec 015: status do atendente (disponível / pausa / ausente) */}
              <PresenceChip />
              {/* spec 036: Novidades, ao lado do sino */}
              <NewsMegaphone audience="staff" />
              <NotificationsBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Menu do usuário"
                    className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    {initial}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex items-center gap-2.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                      {initial}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{user?.name}</span>
                      <span className="block truncate text-xs font-normal text-muted-foreground">
                        {user?.email}
                      </span>
                    </span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void navigate({ to: '/account' })}>
                    <UserRound />
                    Minha conta
                  </DropdownMenuItem>
                  {/* só para quem está em mais de uma empresa: limpar a escolha faz o
                      /me voltar a pedir, e o AuthGate mostra o seletor */}
                  {getKnownTenants().length > 1 ? (
                    <DropdownMenuItem
                      onSelect={() => {
                        clearSelectedTenant();
                        window.location.reload();
                      }}
                    >
                      <ArrowLeftRight />
                      Trocar empresa
                      <span className="ml-auto text-xs text-muted-foreground">
                        {tenantLabel(getSelectedTenant() ?? '')}
                      </span>
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem disabled title="Em breve">
                    <SunMoon />
                    Tema
                    <span className="ml-auto text-xs text-muted-foreground">Em breve</span>
                  </DropdownMenuItem>
                  {/* spec 017 (US3): layout por pessoa, padrão do tenant. O
                      Simplificado é montado pela 018 — até lá, "em breve". */}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <LayoutTemplate className="size-3.5" />
                    Interface
                  </DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={ui.layout}
                    onValueChange={(value) => ui.update({ layout: value as UiLayout })}
                  >
                    {UI_LAYOUTS.map((layout) => {
                      const soon = layout === 'simplified' && !SIMPLIFIED_LAYOUT_AVAILABLE;
                      return (
                        <DropdownMenuRadioItem
                          key={layout}
                          value={layout}
                          disabled={!ui.ready || soon}
                        >
                          {UI_LAYOUT_LABELS[layout]}
                          {soon ? (
                            <span className="ml-auto text-xs text-muted-foreground">Em breve</span>
                          ) : layout === ui.defaultLayout ? (
                            <span className="ml-auto text-xs text-muted-foreground">padrão</span>
                          ) : null}
                        </DropdownMenuRadioItem>
                      );
                    })}
                  </DropdownMenuRadioGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleLogout}>
                    <LogOut />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="min-h-0 flex-1 overflow-y-auto">
            <div className="p-4">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
